import {EventEmitter, Inject, Injectable} from '@angular/core';
import {environment} from '../environments/environment';

import {MatSnackBar, MatSnackBarRef} from '@angular/material/snack-bar';
import {NotificationsComponent} from './notifications/notifications.component';
import {WitsService} from './services/wits.service';
import {UnitParsePipe} from './pipes/unit-parse.pipe';
import {ReplaySubject, Subject} from 'rxjs';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {LocalStorageService} from 'ngx-localstorage';
import {ConfirmDeleteDialogComponent} from './dashboard/modals/confirm-delete-dialog/confirm-delete-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {ErrorFromStream} from './stream-errors/models';
import {DOCUMENT} from '@angular/common';
import {JobLastMessageTrack, MessageCounts} from './dashboard/job-chat/models/message';

import * as moment from 'moment-timezone';

@Injectable()
export class AppConfig {

  unitTypes = [
    {
      id: '0',
      name: 'Metric System (SI)'
    },
    {
      id: '1',
      name: 'Field System (US Customary)'
    }
  ];

  version: string;
  smartAppType: 'MPDSmart' | 'PMDSmart';

  refCheck: Subject<void> = new Subject<void>();

  errorMessages$: ReplaySubject<ErrorFromStream[]> = new ReplaySubject<ErrorFromStream[]>(1);
  dismissedMessages$: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);

  appOffline$ = new ReplaySubject<boolean>(1);


  appVersion: string;
  refCheck$ = new Subject<void>();
  streamConnection: any;

  audio: HTMLAudioElement;
  audioNoStream: HTMLAudioElement;
  mobileFullscreen: boolean;
  commonDateFormat = 'YYYY-MM-DD HH:mm:ss';

  streamErrorSafeList: string[] = [];

  _streamStatus: 'connected' | 'connecting' | 'fail';

  streamConnected$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  timezoneId: string | null;

  get streamStatus() {
    return this._streamStatus;
  }

  set streamStatus(value) {
    this._streamStatus = value;

    if (value === 'connected') {
      this.streamConnected$.next(true);
    }

    if (value === 'fail') {
      this.streamConnected$.next(false);
    }

  }

  currentStreamTimestamp: number;

  httpRetryAttempts = 5;

  isTablet: any;
  currentStreamDisconnectSeconds = 0;
  isChatStreamConnected$ = new ReplaySubject<boolean>(1);

  newMessageCounter$ = new ReplaySubject<MessageCounts>(1);
  lastMessageUpdated$ = new ReplaySubject<JobLastMessageTrack>(1);

  darkMode$ = new ReplaySubject<boolean>(1); 

  set collapsed(value) {
    if (value) {
      document.body.classList.add('collapsed');
    } else {
      document.body.classList.remove('collapsed');
    }
  }

  get collapsed() {
    return document.body.classList.contains('collapsed');
  }


  get isDarkMode(): boolean {
    if (this.localStorageService.get('isDarkMode')) {
      return this.localStorageService.get('isDarkMode');
    } else {
      return false;
    }
  }

  set isDarkMode(value: boolean) {
    if (value) {
      document.body.classList.remove('light-mode');
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
      document.body.classList.add('light-mode');
    }
    this.localStorageService.set('isDarkMode', value);
    this.handleStyles();

    this.darkMode$.next(value);
  }

  get isSmallScreen() {
    return this.isMobile;
  }

  host = environment.host;
  tableHost = environment.tableHost;
  streamHost = environment.streamHost;

  mainLoading: boolean;
  durationInSeconds = 4;
  windowHeight: number;
  cementRefresh$ = new Subject<void>();
  streamDisconnectEvent: EventEmitter<void> = new EventEmitter<void>();
  streamReconnectEvent$: EventEmitter<void> = new EventEmitter<void>();
  changeStyle: EventEmitter<void> = new EventEmitter<void>();

  defaultUnit = '0';
  unitChanged$ = new Subject<any>();
  checkStream$ = new Subject<void>();

  style;

  isMobile: boolean;
  isLandscape: boolean;

  sizeChanged$: Subject<void> = new Subject<void>();

  moment: any;

  constructor(
    public dialog: MatDialog,
    public localStorageService: LocalStorageService,
    public breakpointObserver: BreakpointObserver,
    public unitParsePipe: UnitParsePipe,
    private witsService: WitsService,
    @Inject(DOCUMENT) public document: any,
    private _snackBar: MatSnackBar
  ) {

    this.moment = moment;

    this.dismissedMessages$.next([]);

    this.appVersion = environment.version;

    this.audio = new Audio('assets/sound/alarm_banner.mp3');
    this.audio.load();

    this.audioNoStream = new Audio('assets/sound/alarm_banner.mp3');
    this.audioNoStream.load();

    breakpointObserver
      .observe([
        '(orientation: portrait)',
        '(orientation: landscape)',
        Breakpoints.XSmall,
        Breakpoints.Small,
        Breakpoints.Medium,
        Breakpoints.Large,
        Breakpoints.XLarge,
      ])
      .subscribe(result => {

        this.isMobile = result.breakpoints[Breakpoints.XSmall] || result.breakpoints[Breakpoints.Small];
        this.isLandscape = result.breakpoints['(orientation: landscape)'];


        this.isTablet = result.breakpoints[Breakpoints.Tablet] || result.breakpoints[Breakpoints.TabletPortrait]

        this.sizeChanged$.next();

        if (this.isMobile) {
          this.document.body.classList.add('mobile');
        } else {
          this.document.body.classList.remove('mobile');
        }

        if (this.isLandscape) {
          this.document.body.classList.add('landscape');
        } else {
          this.document.body.classList.remove('landscape');
        }

        this.refCheck.next();
      });
  }

  handleStyles() {
    if (!this.isDarkMode) {
      this.style = 'mapbox://styles/solarnet7/ckclxzidp00qg1ilh4fisojtr';
    } else {
      this.style = 'mapbox://styles/solarnet7/ckkfgq1x101x517nocuiqryy7';
    }
    this.changeStyle.next();
  }

  openSnackBar(message, isSuccess?) {
    let panelClass = [];

    if (isSuccess) {
      panelClass.push('success-state');
    } else {
      panelClass.push('error-state');
    }
    this._snackBar.openFromComponent(NotificationsComponent, {
      duration: this.durationInSeconds * 1000,
      data: message,
      panelClass
    });
  }

  openStreamErrorSnackBar(messages: string[]) {

    this.errorMessages$.next(messages.map((m) => {
      return {
        title: m
      }
    }));

    for (let message of messages) {

      if (this.streamErrorSafeList.includes(message)) {
        continue;
      }

      this.streamErrorSafeList.push(message);
    }

  }

  getFieldMeasure(unit: any, isSI?) {
    let measure = this.witsService.units.find((o) => o.id === unit);
    if (measure) {
      if (isSI) {
        return measure.measure;
      } else {
        return measure.field;
      }
    }
  }

  openConfirmDialog(desc?: string, cancelButton?: string, confirmButton?: string, title?: string): Promise<'confirm' | 'reject'> {
    return new Promise((resolve) => {
      let config = {
        width: '300px',
        data: {} as any
      };
      if (desc) {
        config.data.desc = desc;
      }

      if (cancelButton) {
        config.data.cancel = cancelButton;
      }

      if (confirmButton) {
        config.data.confirmButton = confirmButton;
      }

      if (title) {
        config.data.title = title;
      }

      const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, config);
      dialogRef.afterClosed().subscribe(async (result) => {
        if (result === 'confirm') {
          return resolve('confirm');
        } else {
          return resolve('reject');
        }
      });
    });
  }

  toggleFullscreenOnElement(el) {
    let docElm = el;

    if (!this.document.fullscreenElement) {
      if (docElm.requestFullscreen) {
        docElm.requestFullscreen();
      } else if (docElm.mozRequestFullScreen) {
        docElm.mozRequestFullScreen();
      } else if (docElm.webkitRequestFullScreen) {
        docElm.webkitRequestFullScreen();
      } else if (docElm.msRequestFullscreen) {
        docElm.msRequestFullscreen();
      }
    } else {
      if (this.document.exitFullscreen) {
        this.document.exitFullscreen();
      } else if (this.document.webkitExitFullscreen) {
        this.document.webkitExitFullscreen();
      } else if (this.document.mozCancelFullScreen) {
        this.document.mozCancelFullScreen();
      } else if (this.document.msExitFullscreen) {
        this.document.msExitFullscreen();
      }
    }
  }

  isElementFullscreen() {
    return this.document.fullscreenElement;
  }

  isPwaApp() {
    return window.matchMedia('(display-mode: standalone)').matches;
  }
}
